import {
  AuthDivider,
  AuthFooterLink,
  AuthInput,
  AuthMessage,
  AuthPasswordInput,
  AuthSubmitButton,
  GoogleAuthButton,
  Select,
  SelectItem,
} from '@protoss/ui';
import * as React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard, AuthHeader, AuthLayout } from '../../components/auth';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicAppConfig } from '../../contexts/PublicAppConfigContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { apiFetch } from '../../lib/apiClient';
import { getPreAuthConfig } from '../../lib/preAuthConfig';
import { withDevWorkspaceParam } from '../../lib/url';
import './auth-pages.css';

/**
 * Sign In Page
 *
 * Supports Google OAuth (primary) and email/password (secondary).
 * Auth logic is delegated to useAuth() - no provider-specific code here.
 */

interface FormData {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
}

type FormState = 'idle' | 'loading' | 'error';

export function SignInPage() {
  const {
    config: { productName },
  } = usePublicAppConfig();
  const showGoogleAuth = getPreAuthConfig().allowedMethods.includes('google');
  const showPasswordAuth = getPreAuthConfig().allowedMethods.includes('password');
  const { signInWithGoogle, signInWithPassword, signOut } = useAuth();
  const { refetchModules } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get('inviteId');
  const inviteEmail = searchParams.get('email');

  // DEV-only workspace switcher state
  const [devWorkspaces, setDevWorkspaces] = React.useState<{ slug: string; name: string }[]>([]);
  const devWorkspaceSlug = import.meta.env.DEV ? (searchParams.get('workspace') ?? '') : '';

  React.useEffect(() => {
    if (!import.meta.env.DEV) return;
    fetch('/api/auth/workspaces')
      .then((r) => r.json())
      .then(({ workspaces }: { workspaces: { slug: string; name: string }[] }) =>
        setDevWorkspaces(workspaces)
      )
      .catch(() => {});
  }, []);

  const handleDevWorkspaceChange = (slug: string) => {
    const resolved = slug === '_default' ? '' : slug;
    const next = new URLSearchParams(window.location.search);
    if (resolved) {
      next.set('workspace', resolved);
    } else {
      next.delete('workspace');
    }
    window.location.assign(`/signin?${next.toString()}`);
  };

  // Redirect to the page the user originally intended to visit, or home
  const from = (location.state as { from?: Location })?.from?.pathname ?? '/';

  // Form state
  const [formData, setFormData] = React.useState<FormData>({
    email: inviteEmail ?? '',
    password: '',
  });

  React.useEffect(() => {
    if (inviteEmail) {
      setFormData((prev) => ({ ...prev, email: inviteEmail }));
    }
  }, [inviteEmail]);

  const [errors, setErrors] = React.useState<FormErrors>({});
  const [formState, setFormState] = React.useState<FormState>('idle');
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = React.useState(false);

  // Validation
  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return 'Password is required';
    }
    if (password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      email: validateEmail(formData.email),
      password: validatePassword(formData.password),
    };

    setErrors(newErrors);
    return !newErrors.email && !newErrors.password;
  };

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Clear general error when user modifies form
    if (generalError) {
      setGeneralError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setGeneralError(null);

    if (!validateForm()) {
      return;
    }

    setFormState('loading');

    try {
      await signInWithPassword(formData.email, formData.password);
      if (inviteId) {
        await apiFetch('/api/auth/accept-invite', {
          method: 'POST',
          body: JSON.stringify({ inviteId }),
        });
        await refetchModules();
      }
      navigate(from, { replace: true });
    } catch (error) {
      if (inviteId) {
        await signOut().catch(() => undefined);
      }
      setFormState('error');
      const message = error instanceof Error ? error.message : 'UNKNOWN_ERROR';
      if (message === 'NO_WORKSPACE_ACCESS') {
        setGeneralError(
          'This account does not have access to an active workspace. Contact your workspace admin.'
        );
      } else if (
        message === 'INVITE_EXPIRED' ||
        message === 'INVITE_REVOKED' ||
        message === 'INVITE_EMAIL_MISMATCH'
      ) {
        setGeneralError(
          'This invitation is no longer valid. Ask your workspace admin to send a new invite.'
        );
      } else {
        setGeneralError(message);
      }
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setGeneralError(null);

    try {
      await signInWithGoogle();
      if (inviteId) {
        await apiFetch('/api/auth/accept-invite', {
          method: 'POST',
          body: JSON.stringify({ inviteId }),
        });
        await refetchModules();
      }
      navigate(from, { replace: true });
    } catch (error) {
      if (inviteId) {
        await signOut().catch(() => undefined);
      }
      setIsGoogleLoading(false);
      const message = error instanceof Error ? error.message : 'UNKNOWN_ERROR';
      setGeneralError(
        message === 'NO_WORKSPACE_ACCESS'
          ? 'This account does not have access to an active workspace. Contact your workspace admin.'
          : 'Unable to connect with Google. Please try again.'
      );
    }
  };

  const isLoading = formState === 'loading';

  return (
    <AuthLayout>
      <AuthCard>
        <AuthHeader
          title={`Sign in to ${productName}`}
          subtitle="Access your operational workspace"
        />

        <div className="auth-form-container">
          {inviteId && inviteEmail && (
            <AuthMessage variant="info">
              Sign in with {inviteEmail} to complete your workspace invitation.
            </AuthMessage>
          )}

          {/* Google OAuth */}
          {showGoogleAuth && (
            <>
              <GoogleAuthButton onClick={handleGoogleSignIn} isLoading={isGoogleLoading}>
                Continue with Google
              </GoogleAuthButton>

              {showPasswordAuth && <AuthDivider />}
            </>
          )}

          {/* General error message */}
          {generalError && <AuthMessage variant="error">{generalError}</AuthMessage>}

          {/* Email/Password Form */}
          {showPasswordAuth && (
            <form onSubmit={handleSubmit} className="auth-form" noValidate>
              <AuthInput
                type="email"
                name="email"
                label="Email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleInputChange}
                error={errors.email}
                autoComplete="email"
                disabled={isLoading}
              />

              <AuthPasswordInput
                name="password"
                label="Password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange}
                error={errors.password}
                autoComplete="current-password"
                disabled={isLoading}
              />

              <div className="auth-forgot-link">
                <AuthFooterLink href={withDevWorkspaceParam('/forgot-password')}>
                  Forgot password?
                </AuthFooterLink>
              </div>

              <AuthSubmitButton isLoading={isLoading}>Sign In</AuthSubmitButton>
            </form>
          )}
        </div>

        {showPasswordAuth && (
          <div className="auth-footer-text">
            Don't have an account? <AuthFooterLink href="/signup">Sign up</AuthFooterLink>
          </div>
        )}

        {import.meta.env.DEV && devWorkspaces.length > 1 && (
          <div className="auth-dev-workspace-picker">
            <span className="auth-dev-workspace-picker__label">Dev workspace</span>
            <Select
              value={devWorkspaceSlug || '_default'}
              onValueChange={handleDevWorkspaceChange}
              size="comfortable"
            >
              <SelectItem value="_default">default (first)</SelectItem>
              {devWorkspaces.map((ws) => (
                <SelectItem key={ws.slug} value={ws.slug}>
                  {ws.name}
                </SelectItem>
              ))}
            </Select>
          </div>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
